/**
 * Check-in existing flow: get OneDrive download URL -> POST check-in-existing
 * (backend fetches bytes from `fileUrl`) -> optional delete OneDrive doc.
 * Does not depend on legacy checkin/document reducers.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import AuthTeams from "../../../auth/AuthTeams";
import UserAuth from "../../../auth/userAuth";
import {
  checkInExistingDocument,
  deleteOneDriveDoc,
  updateOneDriveMetadata,
  getCheckinExistingTimeoutMs,
} from "../../api/documentsAPI";
import { sanitizeFileName } from "../../../functions/helperMethod";
import { logger } from "../../../utils/logger";
import { CHECKED_OUT_ALREADY_MESSAGE } from "../../constants/messageTypes";

const userAuth = new UserAuth();
const authTeams = new AuthTeams();

const STATUS_IDLE = "idle";
const STATUS_DOWNLOADING = "downloading";
const STATUS_UPLOADING = "uploading";
const STATUS_DELETING = "deleting";
const STATUS_SUCCESS = "success";
const STATUS_ERROR = "error";

/** Action type to set current step from inside the thunk (so dialog can show step-specific message) */
export const SET_CHECKIN_EXISTING_STEP = "documents/checkinExistingFlow/setStep";

function getValidValue(val, fallback) {
  return val !== undefined && val !== null && val !== "" ? val : fallback;
}

function resolveOneDriveFileSizeBytes(oneDriveDocument) {
  const raw = oneDriveDocument?.size ?? oneDriveDocument?.Size;
  if (typeof raw === "number" && raw > 0) return raw;
  if (typeof raw === "string" && /^\d+$/.test(raw.trim())) {
    return Number(raw.trim());
  }
  return 0;
}

// Note: file bytes are intentionally NOT processed in the browser.
// The server resolves OneDrive (when oneDriveDocumentId is sent) and downloads bytes server-side.

/**
 * Perform check-in existing.
 * @param {object} payload
 * @param {object} payload.documentRevision - IFS revision (docClass, docNo, docRev, docSheet, Title, FormatSize)
 * @param {object} payload.oneDriveDocument - { id, name } (Teams file id and display name)
 * @param {string} payload.teamId
 * @param {string} payload.channelId
 * @param {string} payload.tenantId
 * @param {string} payload.oneDriveRoot - e.g. "ChannelDoc" or "MainMenu"
 * @param {string} [payload.membershipType] - for delete (e.g. "Private", "Regular")
 * @param {boolean} [payload.createViewCopy]
 * @param {boolean} [payload.checkinViewCopy]
 * @param {string} [payload.docReleaseStatus] - release status or ""
 * @param {Array} [payload.objectConnections]
 * @param {Array<{ name: string, base64str?: string }>} [payload.viewCopyFiles]
 * @param {boolean} [payload.deleteAfterSuccess]
 */
export const performCheckinExisting = createAsyncThunk(
  "documents/checkinExistingFlow/perform",
  async (
    {
      documentRevision,
      oneDriveDocument,
      teamId,
      channelId,
      tenantId,
      oneDriveRoot,
      membershipType,
      createViewCopy = false,
      checkinViewCopy = false,
      docReleaseStatus = "",
      objectConnections = [],
      viewCopyFiles = [],
      deleteAfterSuccess = false,
    },
    { dispatch, rejectWithValue }
  ) => {
    if (!documentRevision || !oneDriveDocument?.id) {
      return rejectWithValue({ message: "Missing documentRevision or oneDriveDocument.id" });
    }

    const accessToken = (await userAuth.getUserToken(true))?.accessToken;
    if (!accessToken) {
      return rejectWithValue({ message: "No access token" });
    }

    const documentId = oneDriveDocument.id;
    const docRev = documentRevision;
    const webUser = authTeams.getEmail();
    const oneDriveUserId = authTeams.getUserId();
    const docReleaseStatusStr = docReleaseStatus === false || docReleaseStatus == null ? "" : String(docReleaseStatus);

    try {
      dispatch({ type: SET_CHECKIN_EXISTING_STEP, payload: STATUS_UPLOADING });

      const fileName = sanitizeFileName(
        oneDriveDocument.name || `${getValidValue(docRev.Title, "document")} (${docRev.docClass} - ${docRev.docNo} - ${docRev.docSheet ?? 1} - ${docRev.docRev}) - 1.${docRev.FileExtension || "DOCX"}`
      );
      const body = {
        // Manual PDF: backend checks in original first, then view copy (faster than one combined IFS call).
        checkinViewCopy: false,
        docClass: docRev.docClass,
        docNo: docRev.docNo,
        docRev: docRev.docRev,
        title: encodeURIComponent(getValidValue(docRev.Title, "Document")),
        docFormat: getValidValue(docRev.FormatSize, "*"),
        docSheet: String(getValidValue(docRev.docSheet, "1")),
        fileName,
        oneDriveDocumentId: String(documentId),
        oneDriveRoot: oneDriveRoot ?? "",
        oneDriveTeamId: teamId ?? "",
        oneDriveChannelId: channelId ?? "",
        oneDriveTenantId: tenantId ?? "",
        oneDriveUserId: oneDriveUserId ?? "",
        objectConnections: Array.isArray(objectConnections) ? objectConnections : [],
        createViewCopy,
        rollbackRevisionOnFail: false,
        webUser,
        status: docReleaseStatusStr,
      };

      const viewCopyFile =
        Array.isArray(viewCopyFiles) &&
        viewCopyFiles.length > 0 &&
        typeof viewCopyFiles[0]?.base64str === "string" &&
        viewCopyFiles[0].base64str.trim() !== ""
          ? viewCopyFiles[0]
          : null;
      if (viewCopyFile) {
        const vcName = sanitizeFileName(viewCopyFile.name ?? "");
        if (vcName) {
          body.viewCopyFileName = vcName;
          body.viewCopyFileData = viewCopyFile.base64str;
        }
      }

      const fileSizeBytes = resolveOneDriveFileSizeBytes(oneDriveDocument);
      const viewCopyBase64Length =
        typeof viewCopyFile?.base64str === "string"
          ? viewCopyFile.base64str.length
          : 0;
      const checkinTimeoutMs = getCheckinExistingTimeoutMs({
        fileSizeBytes,
        viewCopyBase64Length,
      });

      const { data: checkinData, status: checkinStatus } =
        await checkInExistingDocument(accessToken, body, {
          timeout: checkinTimeoutMs,
          fileSizeBytes,
          viewCopyBase64Length,
        });

      if (checkinStatus === 203) {
        return rejectWithValue({
          message: CHECKED_OUT_ALREADY_MESSAGE,
        });
      }
      if (checkinStatus !== 200) {
        const msg = typeof checkinData?.message === "string" ? checkinData.message : checkinData?.errorMessage ?? "Check-in failed";
        return rejectWithValue({ message: msg });
      }

      // Update metadata on the OneDrive file after successful check-in
      // (only when the file is NOT scheduled for deletion)
      if (!deleteAfterSuccess) {
        try {
          const userId = authTeams.getUserId();
          await updateOneDriveMetadata(accessToken, {
            userId: userId ?? "",
            tenantId: tenantId ?? "",
            teamId: teamId ?? "",
            channelId: channelId ?? "",
            oneDriveRoot: oneDriveRoot ?? "",
            documentId: String(documentId),
            webUser: webUser ?? "",
              metadata: {
                docClass: docRev.docClass ?? "",
                docNo: docRev.docNo ?? "",
                docRev: docRev.docRev ?? "",
                docSheet: String(docRev.docSheet ?? "1"),
                state: docReleaseStatusStr,
                fileState: "CheckedIn",
                owner: webUser ?? "",
                lastActionDate: new Date().toISOString(),
                isDownloaded: false,
              },
          });
        } catch (metaErr) {
          logger.error("checkinExisting metadata update error", metaErr, "REDUX");
        }
      }

      let deletedAfterCheckin = false;
      if (deleteAfterSuccess) {
        dispatch({ type: SET_CHECKIN_EXISTING_STEP, payload: STATUS_DELETING });
        try {
          await deleteOneDriveDoc(accessToken, {
            fileId: documentId,
            teamId: teamId ?? "",
            membershipType: membershipType ?? "Regular",
            oneDriveRoot: oneDriveRoot ?? "",
            channelId: channelId ?? "",
          });
          deletedAfterCheckin = true;
        } catch (err) {
            logger.error("Error parsing check-in response", err, "REDUX");
        }
      }

      return {
        data: checkinData,
        status: checkinStatus,
        deletedAfterCheckin,
      };
    } catch (err) {
      logger.error("performCheckinExisting failed", err, "REDUX");
      const msg = err?.response?.data?.message ?? err?.message ?? "Check-in failed";
      return rejectWithValue({ message: msg });
    }
  }
);

const initialState = {
  status: STATUS_IDLE,
  step: STATUS_DOWNLOADING,
  message: null,
  loading: false,
  success: false,
  fileSizeBytes: 0,
  startedAt: null,
  /** Set on success so All Documents can highlight the checked-in row (same as check-in new revision). */
  lastDocumentRevision: null,
};

const checkinExistingFlowSlice = createSlice({
  name: "documents/checkinExistingFlow",
  initialState,
  reducers: {
    clearCheckinExistingFlow: (state) => {
      state.status = STATUS_IDLE;
      state.step = STATUS_DOWNLOADING;
      state.message = null;
      state.loading = false;
      state.success = false;
      state.fileSizeBytes = 0;
      state.startedAt = null;
      state.lastDocumentRevision = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(SET_CHECKIN_EXISTING_STEP, (state, action) => {
        if (action.payload) state.step = action.payload;
      })
      .addCase(performCheckinExisting.pending, (state, action) => {
        state.loading = true;
        state.status = STATUS_DOWNLOADING;
        state.step = STATUS_DOWNLOADING;
        state.message = null;
        state.success = false;
        state.fileSizeBytes = resolveOneDriveFileSizeBytes(action.meta?.arg?.oneDriveDocument);
        state.startedAt = Date.now();
        state.lastDocumentRevision = null;
      })
      .addCase(performCheckinExisting.fulfilled, (state, action) => {
        state.loading = false;
        state.status = STATUS_SUCCESS;
        state.message = null;
        state.success = true;
        state.lastDocumentRevision = action.meta?.arg?.documentRevision ?? null;
      })
      .addCase(performCheckinExisting.rejected, (state, action) => {
        state.loading = false;
        state.status = STATUS_ERROR;
        state.message = action.payload?.message ?? action.error?.message ?? "Check-in failed";
        state.success = false;
      });
  },
});

export const { clearCheckinExistingFlow } = checkinExistingFlowSlice.actions;
export const CHECKIN_EXISTING_STATUS = {
  IDLE: STATUS_IDLE,
  DOWNLOADING: STATUS_DOWNLOADING,
  UPLOADING: STATUS_UPLOADING,
  DELETING: STATUS_DELETING,
  SUCCESS: STATUS_SUCCESS,
  ERROR: STATUS_ERROR,
};
export default checkinExistingFlowSlice.reducer;
